/**
 * MfaSettingsPanel
 *
 * Handles all MFA management for the current authenticated user:
 *   • Displays current method status (Email OTP / Authenticator App / disabled)
 *   • Guided TOTP setup: QR-scan tab + manual-entry tab, 30 s countdown, 6-box OTP confirm
 *   • Resumes in-progress TOTP setup (setupPending=true)
 *   • Reconfigure app (re-run TOTP setup when already active)
 *   • Switch TOTP → Email OTP (one-click)
 *   • Disable MFA (password confirmation)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Smartphone,
} from "lucide-react";
import {
  confirmTotpSetup,
  disableMfa,
  enableEmailMfa,
  getMfaStatus,
  initTotpSetup,
} from "#/features/auth/api";
import type { MfaStatusResponse, TotpInitResponse } from "#/features/auth/types";
import { useAuth } from "#/context/AuthProvider";
import { OtpInput } from "#/components/common/OtpInput";

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiError(err: any): string {
  return (
    err?.response?.data?.message ||
    err?.message ||
    "An unexpected error occurred."
  );
}

/** Format a Base32 secret into groups of 4 for readability: JBSW Y3DP EHPK 3PXP */
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

// ── 30-second TOTP window countdown ──────────────────────────────────────────

function useTotpCountdown() {
  const [s, setS] = useState(() => 30 - (Math.floor(Date.now() / 1000) % 30));
  useEffect(() => {
    const id = setInterval(() => setS(30 - (Math.floor(Date.now() / 1000) % 30)), 1_000);
    return () => clearInterval(id);
  }, []);
  return s;
}

function TotpCountdown() {
  const s = useTotpCountdown();
  const urgent = s <= 7;
  const pct = (s / 30) * 100;
  const r = 9;
  const circ = 2 * Math.PI * r;
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${urgent ? "text-amber-600" : "text-slate-500"}`}>
      <svg width="22" height="22" viewBox="0 0 22 22" className="-rotate-90">
        <circle cx="11" cy="11" r={r} fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
        <circle
          cx="11" cy="11" r={r} fill="none"
          stroke={urgent ? "#d97706" : "#10b981"}
          strokeWidth="2.5"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct / 100)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <span>{urgent ? `Code expires in ${s}s — wait for refresh` : `Code refreshes in ${s}s`}</span>
    </div>
  );
}

// ── Panel type ────────────────────────────────────────────────────────────────

type ActivePanel = "idle" | "totp-setup" | "email-setup" | "switch-email" | "disable";

// ── Main component ────────────────────────────────────────────────────────────

type MfaSettingsPanelProps = {
  /** Skip the method picker and jump directly to a panel on first render. */
  initialPanel?: ActivePanel;
};

export function MfaSettingsPanel({ initialPanel }: MfaSettingsPanelProps = {}) {
  const { setMfaEnabled, currentUser } = useAuth();
  const [status, setStatus]           = useState<MfaStatusResponse | null>(null);
  const [loading, setLoading]         = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<ActivePanel>(initialPanel ?? "idle");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setStatusError(null);
    try {
      setStatus(await getMfaStatus());
    } catch (err) {
      setStatusError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  function onSuccess(nowEnabled: boolean) {
    setMfaEnabled(nowEnabled);
    setActivePanel("idle");
    void loadStatus();
  }

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (statusError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {statusError}
        <button onClick={() => void loadStatus()} className="ml-2 font-medium underline">Retry</button>
      </div>
    );
  }

  const s = status!;

  // ── Inline panels ──────────────────────────────────────────────────────────
  if (activePanel === "totp-setup") {
    return (
      <TotpSetupPanel
        userEmail={currentUser?.email ?? ""}
        onSuccess={() => onSuccess(true)}
        onCancel={() => setActivePanel("idle")}
      />
    );
  }
  if (activePanel === "email-setup") {
    return (
      <EmailSetupPanel
        onSuccess={() => onSuccess(true)}
        onCancel={() => setActivePanel("idle")}
      />
    );
  }
  if (activePanel === "switch-email") {
    return (
      <SwitchToEmailPanel
        onSuccess={() => onSuccess(true)}
        onCancel={() => setActivePanel("idle")}
      />
    );
  }
  if (activePanel === "disable") {
    return (
      <DisablePanel
        onSuccess={() => onSuccess(false)}
        onCancel={() => setActivePanel("idle")}
      />
    );
  }

  // ── Status + action view ──────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <StatusBadge status={s} />

      {/* Resume pending TOTP setup */}
      {s.enabled && s.type === "EMAIL" && s.setupPending && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">Setup not finished</p>
            <p className="text-amber-800">
              You started authenticator app setup but didn't complete it.
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => setActivePanel("totp-setup")}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Complete setup
              </button>
              <button
                onClick={() => setActivePanel("totp-setup")}
                className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                Start over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions based on current state */}
      {!s.enabled && (
        <MethodPicker
          onPickTotp={() => setActivePanel("totp-setup")}
          onPickEmail={() => setActivePanel("email-setup")}
        />
      )}

      {s.enabled && s.type === "EMAIL" && !s.setupPending && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            For stronger security, switch to an authenticator app — it works without
            internet and isn't vulnerable to email interception.
          </p>
          <div className="flex flex-wrap gap-2">
            <ActionButton
              icon={<Smartphone className="h-4 w-4" />}
              label="Set up Authenticator App"
              variant="primary"
              onClick={() => setActivePanel("totp-setup")}
            />
            <ActionButton
              icon={<ShieldOff className="h-4 w-4" />}
              label="Disable MFA"
              variant="danger"
              onClick={() => setActivePanel("disable")}
            />
          </div>
        </div>
      )}

      {s.enabled && s.type === "TOTP" && (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            icon={<RefreshCw className="h-4 w-4" />}
            label="Reconfigure App"
            variant="secondary"
            onClick={() => setActivePanel("totp-setup")}
          />
          <ActionButton
            icon={<Mail className="h-4 w-4" />}
            label="Switch to Email OTP"
            variant="secondary"
            onClick={() => setActivePanel("switch-email")}
          />
          <ActionButton
            icon={<ShieldOff className="h-4 w-4" />}
            label="Disable MFA"
            variant="danger"
            onClick={() => setActivePanel("disable")}
          />
        </div>
      )}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: MfaStatusResponse }) {
  const isTotp  = status.enabled && status.type === "TOTP";
  const isEmail = status.enabled && status.type === "EMAIL";

  return (
    <div className={`flex items-start gap-4 rounded-2xl border p-5 ${
      status.enabled ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"
    }`}>
      <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
        isTotp  ? "bg-emerald-100 text-emerald-700" :
        isEmail ? "bg-blue-100 text-blue-700" :
                  "bg-slate-200 text-slate-500"
      }`}>
        {isTotp ? <Smartphone className="h-5 w-5" /> :
         isEmail ? <Mail className="h-5 w-5" /> :
         <ShieldOff className="h-5 w-5" />}
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900">
          {isTotp  ? "Authenticator app • Active" :
           isEmail ? "Email OTP • Active" :
                     "MFA not enabled"}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {isTotp  ? "6-digit codes from your authenticator app are required at each sign-in." :
           isEmail ? "A one-time code is emailed to you at each sign-in." :
                     "Your account has no second verification step."}
        </p>
      </div>
    </div>
  );
}

// ── Method picker (when MFA disabled) ────────────────────────────────────────

function MethodPicker({
  onPickTotp,
  onPickEmail,
}: {
  onPickTotp: () => void;
  onPickEmail: () => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Choose a verification method:</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          onClick={onPickTotp}
          className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-emerald-400 hover:bg-emerald-50"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">Authenticator App</p>
            <p className="text-xs text-slate-500">Google Authenticator, Authy, 1Password…</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-emerald-600" />
        </button>

        <button
          onClick={onPickEmail}
          className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-400 hover:bg-blue-50"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
            <Mail className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-900">Email OTP</p>
            <p className="text-xs text-slate-500">Receive a code in your inbox at sign-in.</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-blue-600" />
        </button>
      </div>
    </div>
  );
}

// ── Shared action button ──────────────────────────────────────────────────────

function ActionButton({
  icon, label, variant, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  variant: "primary" | "secondary" | "danger";
  onClick: () => void;
}) {
  const cls =
    variant === "primary"
      ? "bg-emerald-600 text-white hover:bg-emerald-700 border-transparent"
      : variant === "danger"
      ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-medium transition ${cls}`}
    >
      {icon}
      {label}
    </button>
  );
}

// ── TOTP Setup panel ──────────────────────────────────────────────────────────

type TotpSetupStep = "loading" | "setup" | "done";
type SetupTab = "qr" | "manual";

function TotpSetupPanel({
  userEmail,
  onSuccess,
  onCancel,
}: {
  userEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [step, setStep]       = useState<TotpSetupStep>("loading");
  const [initData, setInitData] = useState<TotpInitResponse | null>(null);
  const [tab, setTab]         = useState<SetupTab>("qr");
  const [code, setCode]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [copied, setCopied]   = useState(false);
  const copyTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    initTotpSetup()
      .then((data) => { if (!cancelled) { setInitData(data); setStep("setup"); } })
      .catch((err) => { if (!cancelled) setError(apiError(err)); });
    return () => { cancelled = true; };
  }, []);

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (code.length === 6 && step === "setup" && !submitting) void handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  async function handleConfirm() {
    if (code.length < 6) return;
    setSubmitting(true);
    setError(null);
    try {
      await confirmTotpSetup({ code });
      setStep("done");
      setTimeout(onSuccess, 2_000);
    } catch (err) {
      setError(apiError(err));
      setCode(""); // clear so user can enter next window's code without re-generating QR
    } finally {
      setSubmitting(false);
    }
  }

  function copySecret() {
    if (!initData) return;
    void navigator.clipboard.writeText(initData.secret);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 2_000);
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (step === "loading" && !error) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Generating QR code…
      </div>
    );
  }

  // ── Error on init ────────────────────────────────────────────────────────
  if (step === "loading" && error) {
    return (
      <div className="space-y-3 rounded-2xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">{error}</p>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setError(null);
              setStep("loading");
              initTotpSetup()
                .then((data) => { setInitData(data); setStep("setup"); })
                .catch((e) => setError(apiError(e)));
            }}
            className="rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Retry
          </button>
          <button onClick={onCancel} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
        <div>
          <p className="text-sm font-semibold text-emerald-900">Authenticator app enabled!</p>
          <p className="text-xs text-emerald-700">
            From your next sign-in you'll be asked for a 6-digit code from your app.
          </p>
        </div>
      </div>
    );
  }

  // ── Setup screen ──────────────────────────────────────────────────────────
  const formatted = initData ? formatSecret(initData.secret) : "";

  return (
    <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900">Set up Authenticator App</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Compatible with Google Authenticator, Microsoft Authenticator, Authy, 1Password, and any RFC 6238 TOTP app.
          </p>
        </div>
        <button onClick={onCancel} className="ml-4 shrink-0 text-xs text-slate-400 hover:text-slate-600">Cancel</button>
      </div>

      {/* App compatibility chips */}
      <div className="flex flex-wrap gap-1.5">
        {["Google Authenticator", "Microsoft Authenticator", "Authy", "1Password"].map((app) => (
          <span key={app} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
            {app}
          </span>
        ))}
      </div>

      {/* Tabs */}
      <div className="rounded-xl border border-slate-200">
        {/* Tab headers */}
        <div className="flex border-b border-slate-200">
          {(["qr", "manual"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2.5 text-sm font-medium transition ${
                tab === t
                  ? "border-b-2 border-emerald-600 text-emerald-700"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t === "qr" ? "📷 Scan QR Code" : "⌨️ Enter Key Manually"}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-5">
          {tab === "qr" && initData && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={initData.qrCodeDataUrl}
                alt="Scan this QR code with your authenticator app"
                className="h-48 w-48 rounded-xl border border-slate-200"
              />
              <ol className="space-y-1 text-left text-sm text-slate-600">
                <li><span className="font-semibold text-slate-800">1.</span> Open your authenticator app.</li>
                <li><span className="font-semibold text-slate-800">2.</span> Tap <span className="rounded bg-slate-100 px-1 font-mono text-xs">+</span> or <strong>Add account</strong>.</li>
                <li><span className="font-semibold text-slate-800">3.</span> Choose <strong>Scan QR code</strong> and point your camera at the code above.</li>
              </ol>
            </div>
          )}

          {tab === "manual" && initData && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                In your authenticator app, choose <strong>Enter setup key</strong> and fill in:
              </p>
              <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
                <Row label="Account name" value={userEmail || "your-email@eatta.org"} />
                <Row label="Type" value="Time-based" />
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Setup key</p>
                    <p className="mt-0.5 select-all font-mono text-sm font-bold tracking-wider text-slate-900">
                      {formatted}
                    </p>
                  </div>
                  <button
                    onClick={copySecret}
                    title="Copy key"
                    className="mt-1 shrink-0 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                  >
                    {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Verify section */}
      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <p className="text-sm font-semibold text-slate-800">
          Step 2 — Enter the 6-digit code from your app
        </p>
        <OtpInput value={code} onChange={setCode} autoFocus disabled={submitting} />
        <div className="flex justify-center">
          <TotpCountdown />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
            <span className="ml-1 text-red-500">Wait for the next 30-second window and try again.</span>
          </div>
        )}

        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={submitting || code.length < 6}
          className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify & Enable"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800">{value}</p>
    </div>
  );
}

// ── Email OTP setup ───────────────────────────────────────────────────────────

function EmailSetupPanel({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handle() {
    setSubmitting(true);
    setError(null);
    try {
      await enableEmailMfa();
      setDone(true);
      setTimeout(onSuccess, 1_500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <p className="text-sm font-medium text-emerald-800">
          Email OTP enabled. You'll receive a code at each sign-in.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Enable Email OTP</p>
          <p className="mt-0.5 text-xs text-slate-500">
            A one-time code will be sent to your email address at each sign-in.
          </p>
        </div>
      </div>
      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      <div className="flex gap-3">
        <button onClick={() => void handle()} disabled={submitting}
          className="flex-1 rounded-2xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60">
          {submitting ? "Enabling…" : "Enable Email OTP"}
        </button>
        <button onClick={onCancel} disabled={submitting}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Switch TOTP → Email ───────────────────────────────────────────────────────

function SwitchToEmailPanel({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handle() {
    setSubmitting(true);
    setError(null);
    try {
      await enableEmailMfa();
      setDone(true);
      setTimeout(onSuccess, 1_500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <p className="text-sm font-medium text-emerald-800">
          Switched to Email OTP. Your authenticator app registration has been removed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="text-sm font-semibold text-slate-900">Switch to Email OTP?</p>
          <p className="mt-0.5 text-xs text-slate-600">
            Your authenticator app registration will be removed and a code will be emailed
            to you at each sign-in instead. You can always switch back to an app later.
          </p>
        </div>
      </div>
      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      <div className="flex gap-3">
        <button onClick={() => void handle()} disabled={submitting}
          className="flex-1 rounded-2xl bg-amber-600 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:opacity-60">
          {submitting ? "Switching…" : "Switch to Email OTP"}
        </button>
        <button onClick={onCancel} disabled={submitting}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          Keep App
        </button>
      </div>
    </div>
  );
}

// ── Disable MFA ───────────────────────────────────────────────────────────────

function DisablePanel({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handle(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await disableMfa({ password });
      setDone(true);
      setTimeout(onSuccess, 1_500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <ShieldCheck className="h-5 w-5 shrink-0 text-amber-600" />
        <p className="text-sm font-medium text-amber-800">MFA disabled for your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-red-100 bg-red-50/40 p-5">
      <div>
        <p className="text-sm font-semibold text-slate-900">Disable MFA</p>
        <p className="mt-0.5 text-xs text-slate-500">
          Enter your current password to remove two-factor authentication.
        </p>
      </div>
      <form className="space-y-4" onSubmit={handle}>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Current password</label>
          <input
            type="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            required autoComplete="current-password" autoFocus
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-100"
            placeholder="Enter your password"
          />
        </div>
        {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={submitting || !password}
            className="flex-1 rounded-2xl bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60">
            {submitting ? "Disabling…" : "Disable MFA"}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
